import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { MAIN_SITE_URL, categories } from "@/utils/constants";
import { generateArticleJsonLd, generateBreadcrumbJsonLd } from "@/utils/seo-schema";

const articlePath = "/artykul/kakobuy-lista-produktow";
const articleImage = "https://readdy.ai/api/search-image?query=Organized%20grid%20layout%20of%20diverse%20fashion%20product%20categories%20on%20warm%20cream%20backdrop%2C%20sneakers%20hoodies%20jackets%20bags%20accessories%20arranged%20in%20clean%20sections%2C%20soft%20natural%20daylight%2C%20minimalist%20catalog%20photography%2C%20warm%20earthy%20beige%20tones%2C%20comprehensive%20product%20overview%2C%20editorial%20ecommerce%20composition%2C%20organized%20retail%20aesthetic&width=1200&height=800&seq=kakobuy-article-lista&orientation=landscape";

const schemas = [
  generateArticleJsonLd({
    headline: "Kakobuy Lista Produktów — Kompletny Katalog Wszystkich Kategorii",
    description: "Pełna lista kategorii produktów dostępnych w Kakobuy Spreadsheet. Od shoes i hoodies po akcesoria i elektronikę — sprawdź wszystkie możliwości.",
    datePublished: "2026-07-01",
    dateModified: "2026-07-21",
    imageUrl: articleImage,
    articlePath,
  }),
  generateBreadcrumbJsonLd([
    { name: "Strona główna", path: "/" },
    { name: "Kakobuy Lista Produktów", path: articlePath },
  ]),
];

export default function KakobuyListaProduktowArticle() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-600 mb-8 flex-wrap">
              <Link to="/" className="hover:text-foreground-900 transition-colors cursor-pointer">Strona główna</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
              <span className="text-foreground-900 font-medium">Kakobuy Lista Produktów</span>
            </nav>

            <header className="mb-10">
              <div className="inline-flex items-center gap-2 bg-primary-100 px-4 py-1.5 rounded-full mb-4">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-list-check-2 text-primary-500 text-sm"></i></span>
                <span className="text-xs font-semibold text-primary-700 whitespace-nowrap">Katalog kategorii</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                Kakobuy Lista Produktów — Kompletny Katalog Wszystkich Kategorii
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Pełna lista kategorii produktów dostępnych w Kakobuy Spreadsheet. Od shoes i hoodies po akcesoria i elektronikę — sprawdź wszystkie możliwości.
              </p>
              <div className="flex items-center gap-3 text-xs text-foreground-600 flex-wrap">
                <span>Ostatnia aktualizacja: Lipiec 2026</span>
                <span className="text-foreground-400">•</span>
                <span>Czas czytania: 8 minut</span>
              </div>
            </header>

            <div className="w-full h-56 md:h-72 rounded-xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Organized%20grid%20layout%20of%20diverse%20fashion%20product%20categories%20on%20warm%20cream%20backdrop%2C%20sneakers%20hoodies%20jackets%20bags%20accessories%20arranged%20in%20clean%20sections%2C%20soft%20natural%20daylight%2C%20minimalist%20catalog%20photography%2C%20warm%20earthy%20beige%20tones%2C%20comprehensive%20product%20overview%2C%20editorial%20ecommerce%20composition%2C%20organized%20retail%20aesthetic&width=1200&height=800&seq=kakobuy-article-lista&orientation=landscape"
                alt="Kakobuy Lista Produktów — Katalog kategorii"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed space-y-8">
              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Jak działa lista produktów Kakobuy?</h2>
                <p className="mb-3">Kakobuy Spreadsheet to uporządkowany arkusz kalkulacyjny z linkami do produktów dostępnych na platformie. Lista produktów jest podzielona na kilkanaście kategorii, co pozwala szybko znaleźć interesujący produkt bez konieczności ręcznego przeszukiwania platformy.</p>
                <p className="mb-3">Każda kategoria zawiera od kilkudziesięciu do kilkuset produktów, które zostały wybrane na podstawie popularności, jakości i opinii użytkowników. Linki są regularnie aktualizowane, a niedostępne produkty usuwane.</p>
                <p>Poniżej znajdziesz pełną listę wszystkich 14 kategorii dostępnych w arkuszu wraz z krótkim opisem każdej z nich.</p>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Pełna lista kategorii Kakobuy Spreadsheet</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 p-4 bg-background-100 rounded-lg border border-background-200/70 hover:border-accent-300/60 transition-colors cursor-pointer"
                    >
                      <span className="w-10 h-10 flex-shrink-0 rounded-lg overflow-hidden">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </span>
                      <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">Najpopularniejsze kategorie w Polsce</h2>
                <p className="mb-3">Według analizy ruchu w arkuszu, polscy użytkownicy najczęściej przeglądają następujące kategorie:</p>
                <div className="space-y-2">
                  {[
                    { pos: "1", cat: "Shoes", pct: "35%", desc: "Buty to niekwestionowany numer jeden — sneakersy i obuwie sportowe dominują" },
                    { pos: "2", cat: "Hoodies/Sweaters", pct: "20%", desc: "Bluzy i swetry to druga najpopularniejsza kategoria, szczególnie jesienią i zimą" },
                    { pos: "3", cat: "T-Shirts", pct: "15%", desc: "Koszulki basic i graphic tee cieszą się stałym zainteresowaniem przez cały rok" },
                    { pos: "4", cat: "Jackets", pct: "10%", desc: "Kurtki i płaszcze — popularne szczególnie w okresie przejściowym" },
                    { pos: "5", cat: "Accessories", pct: "8%", desc: "Dodatki — okulary, czapki, biżuteria jako uzupełnienie stylizacji" },
                  ].map((row) => (
                    <div key={row.pos} className="flex items-center gap-3 p-3 bg-background-100 rounded-lg border border-background-200/70">
                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 text-xs font-bold text-accent-500 flex-shrink-0">{row.pos}</span>
                      <div className="flex-1">
                        <span className="text-sm font-semibold text-foreground-900">{row.cat}</span>
                        <span className="text-xs text-foreground-600 ml-2">({row.pct} ruchu)</span>
                        <p className="text-xs text-foreground-600 mt-0.5">{row.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="bg-gradient-to-r from-accent-100/60 to-primary-100/60 rounded-2xl p-6 md:p-8 text-center border border-accent-200/40">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">Przeglądaj pełną listę produktów</h2>
                <p className="text-sm text-foreground-700 mb-5 max-w-md mx-auto">Wszystkie kategorie i tysiące produktów czekają na Ciebie w Kakobuy Spreadsheet.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a href={MAIN_SITE_URL} target="_blank" rel="noopener nofollow" className="w-full sm:w-auto bg-accent-500 text-background-50 font-semibold px-8 py-3 rounded-full hover:bg-accent-600 transition-all text-sm whitespace-nowrap cursor-pointer">Otwórz Kakobuy Spreadsheet</a>
                  <Link to="/" className="w-full sm:w-auto bg-background-50 text-foreground-900 font-semibold px-8 py-3 rounded-full border border-background-300/60 hover:bg-background-100 transition-all text-sm whitespace-nowrap cursor-pointer">Powrót na stronę główną</Link>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json" dangerouslySetInnerHTML={{ __html: s }} />
      ))}
    </div>
  );
}